import React, { useState, useEffect, useRef } from 'react';
import MapPinModal from './MapPinModal';

const SelectAndSubmitModal = ({
  isVisible,
  selectedImage,
  businessSuggestions,
  isLoadingSuggestions,
  confirmationData,
  isSubmitting,
  attachmentCount,
  attachments = [],
  setAttachments,
  userGPS,
  onSubmit,
  onEdit,
  onAddNotes,
  onClose,
  showToast,
  playSound
}) => {
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showCameraCapture, setShowCameraCapture] = useState(false);
  const [manualCompanyName, setManualCompanyName] = useState('');
  const [manualAddress, setManualAddress] = useState('');
  const [manualPhone, setManualPhone] = useState('');
  const [submissionNotes, setSubmissionNotes] = useState('');
  const scrollContainerRef = useRef(null);
  const videoRef = useRef(null);
  const [cameraStream, setCameraStream] = useState(null);

  // Auto-select nearest location (first in list) when suggestions load
  useEffect(() => {
    if (businessSuggestions && businessSuggestions.length > 0 && !selectedBusiness) {
      const nearest = businessSuggestions[0];
      console.log('🔍 Auto-selecting nearest business');
      setSelectedBusiness({
        name: nearest.name,
        address: nearest.address,
        phone: nearest.phone,
        fullBusiness: nearest,
        isManual: false
      });
    }
  }, [businessSuggestions, selectedBusiness]);

  // Use confirmationData location if already set
  useEffect(() => {
    if (confirmationData?.address && !selectedBusiness) {
      setSelectedBusiness({
        name: confirmationData.address,
        address: confirmationData.fullBusiness?.address || '',
        phone: confirmationData.fullBusiness?.phone || '',
        fullBusiness: confirmationData.fullBusiness,
        isManual: confirmationData.fullBusiness?.isManual || false
      });
    }
  }, [confirmationData, selectedBusiness]);

  // Clear notes when modal closes
  useEffect(() => {
    if (!isVisible) {
      setSubmissionNotes('');
      setShowNotesModal(false);
    }
  }, [isVisible]);
  
  // Start camera stream when camera capture opens
  useEffect(() => {
    if (showCameraCapture && videoRef.current && cameraStream) {
      videoRef.current.srcObject = cameraStream;
    }
  }, [showCameraCapture, cameraStream]);

  // Auto-center first item when modal opens and pre-select it
  useEffect(() => {
    if (!scrollContainerRef.current || !businessSuggestions || businessSuggestions.length === 0 || !isVisible) return;

    const container = scrollContainerRef.current;
    
    // Longer delay to ensure DOM is fully ready
    const timer = setTimeout(() => {
      // Reset scroll to top first, then scroll to center first item
      // The spacer is 66px, so scrolling to 0 puts first item at top of spacer
      // We want it centered in the 180px container, so scroll to position 0
      container.scrollTop = 0;
      
      // Pre-select first business if nothing selected yet
      if (!selectedBusiness && businessSuggestions[0]) {
        const first = businessSuggestions[0];
        console.log('🎯 PICKER: Auto-selecting first business:', first.name);
        setSelectedBusiness({
          name: first.name,
          address: first.address,
          phone: first.phone,
          fullBusiness: first,
          isManual: false
        });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [businessSuggestions, isVisible]);

  // Scroll snap handler - updates selection when user stops scrolling
  useEffect(() => {
    if (!scrollContainerRef.current || !businessSuggestions || businessSuggestions.length <= 1) return;

    const container = scrollContainerRef.current;
    let scrollTimeout;

    const handleScrollEnd = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        // Find the centered item after scroll ends
        const items = container.querySelectorAll('[data-business-index]');
        const containerCenter = container.getBoundingClientRect().top + container.clientHeight / 2;
        
        let closestItem = null;
        let minDistance = Infinity;
        
        items.forEach((item) => {
          const itemCenter = item.getBoundingClientRect().top + item.clientHeight / 2;
          const distance = Math.abs(containerCenter - itemCenter);
          
          if (distance < minDistance) {
            minDistance = distance;
            closestItem = item;
          }
        });
        
        if (closestItem) {
          const index = parseInt(closestItem.getAttribute('data-business-index'));
          // 🔧 FIX: Removed incorrect +1 offset - businesses render with actual index
          const business = businessSuggestions[index];
          
          console.log(`🔍 Scroll ended at index ${index}`);
          
          if (business && business.name !== selectedBusiness?.name) {
            console.log('🔍 Setting selected business');
            setSelectedBusiness({
              name: business.name,
              address: business.address,
              phone: business.phone,
              fullBusiness: business,
              isManual: false
            });
          }
        }
      }, 150); // Wait for scroll to settle
    };

    container.addEventListener('scroll', handleScrollEnd);
    return () => {
      container.removeEventListener('scroll', handleScrollEnd);
      clearTimeout(scrollTimeout);
    };
  }, [businessSuggestions, selectedBusiness]);

  if (!isVisible) return null;

  const handleManualSubmit = () => {
    const company = manualCompanyName.trim();
    const address = manualAddress.trim();
    const phone = manualPhone.trim();

    // Require at least a company name OR an address
    if (!company && !address) {
      showToast('⚠️ Please enter a company name or address', 'warning');
      return;
    }
    
    const finalName = company || address;
    const finalAddress = address || company || '';

    setSelectedBusiness({
      name: finalName,
      address: finalAddress,
      phone: phone || '',
      fullBusiness: { name: finalName, address: finalAddress, phone: phone || '', isManualEntry: true },
      isManual: true,
      locationSource: 'manual'
    });
    
    setManualCompanyName('');
    setManualAddress('');
    setManualPhone('');
    setShowManualEntry(false);
  };

  const handleSubmit = async () => {
    if (!selectedBusiness) {
      showToast('⚠️ Please select a location', 'warning');
      return;
    }
    
    if (isSubmitting) return;
    
    // Play submission sound
    playSound('camera-shutter', 'select-submit');
    
    // Pass selected business data and notes to parent
    const submissionData = { ...selectedBusiness, submissionNotes };
    await onSubmit(submissionData);
  };

  return (
    <>
      {/* Main Modal */}
      {!showManualEntry && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-[9999] flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-[var(--pbfs-navy)] backdrop-blur-xl rounded-3xl border border-white/10 max-w-lg w-full mx-4 h-[85vh] max-h-[700px] shadow-2xl overflow-hidden animate-scaleIn flex flex-col">
            
            {/* Header with Image Preview */}
            <div className="relative flex-shrink-0">
              {selectedImage && (
                <div className="relative">
                  <img
                    src={selectedImage}
                    alt="Captured"
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[var(--pbfs-navy)]"></div>
                  <div className="absolute top-4 right-4 bg-green-500 text-white text-xs px-3 py-1.5 rounded-full shadow-lg font-medium">
                    ✓ Ready
                  </div>
                </div>
              )}
              
              {/* Title Overlay */}
              <div className="absolute bottom-0 left-0 right-0 px-6 pb-4">
                <h2 className="text-2xl font-bold text-[var(--pbfs-green)] tracking-wide">
                  SELECT & SUBMIT
                </h2>
              </div>
            </div>

            {/* Fixed Content Section - DOES NOT SCROLL */}
            <div className="px-6 pt-4 flex-shrink-0">
              
              {/* Location Label */}
              <div className="text-white/60 text-sm mb-3">
                Selected Location
              </div>

              {/* Selected Location Display */}
              {selectedBusiness && (
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 mb-4 border border-white/20">
                  <div className="text-white text-base leading-relaxed">
                    {selectedBusiness.name}
                  </div>
                  {selectedBusiness.address && selectedBusiness.address !== selectedBusiness.name && (
                    <div className="text-white/50 text-sm mt-1">
                      {selectedBusiness.address}
                    </div>
                  )}
                  {selectedBusiness.phone && (
                    <div className="text-[var(--pbfs-green)] text-sm mt-1">
                      {selectedBusiness.phone}
                    </div>
                  )}
                </div>
              )}

              {/* Location Options - Compact labels */}
              <div className="flex gap-5 mb-3 pb-3 border-b border-white/10 text-sm">
                <button
                  onClick={() => {
                    setShowMapModal(true);
                  }}
                  className="text-[var(--pbfs-blue)] hover:text-[var(--pbfs-blue)]/80 underline transition-colors"
                  title="Pin location on map"
                >
                  📍 Pin
                </button>
                <button
                  onClick={() => setShowManualEntry(true)}
                  className="text-white/60 hover:text-white underline transition-colors"
                  title="Enter address manually"
                >
                  ✏️ Manual
                </button>
                <button
                  onClick={() => setShowNotesModal(true)}
                  className="text-white/60 hover:text-white underline transition-colors relative"
                  title="Add notes & attachments"
                >
                  📎 Notes
                  {(submissionNotes && submissionNotes.trim() !== '') || attachmentCount > 0 ? (
                    <span className="absolute -top-1 -right-2 bg-[var(--pbfs-green)] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                      {attachmentCount + (submissionNotes.trim() ? 1 : 0)}
                    </span>
                  ) : null}
                </button>
              </div>
            </div>

            {/* Scrollable Wheel Section - ONLY THIS SCROLLS */}
            <div className="flex-1 px-6 pb-4 overflow-hidden">
              {isLoadingSuggestions ? (
                <div className="text-center py-6">
                  <div className="text-white/60 text-sm">🔍 Finding nearby businesses...</div>
                </div>
              ) : businessSuggestions && businessSuggestions.length > 0 ? (
                <div className="h-full flex flex-col">
                  <div className="text-white/40 text-xs mb-2">
                    Choose from {businessSuggestions.length} nearby businesses
                  </div>
                  
                  {/* iOS-Style Picker Wheel */}
                  <div className="flex-1 relative" style={{ minHeight: '180px', height: '180px' }}>
                    {/* Center Line Indicator - Centered selection frame */}
                    <div 
                      className="absolute left-2 right-2 h-12 border-2 border-white/60 pointer-events-none z-10 rounded-xl bg-white/5"
                      style={{ top: 'calc(50% - 24px)' }}
                    ></div>
                    
                    {/* Scroll Container with Snap */}
                    <div 
                      ref={scrollContainerRef}
                      className="h-full overflow-y-auto overflow-x-hidden picker-scroll"
                      style={{ 
                        scrollSnapType: 'y mandatory',
                        overscrollBehavior: 'contain'
                      }}
                    >
                      {/* Top spacer - centers first item */}
                      <div style={{ height: '66px' }} aria-hidden="true"></div>
                      
                      {/* Items - Show ALL businesses */}
                      {businessSuggestions.map((business, index) => {
                        // Calculate distance display
                        const distance = business.distance_meters;
                        const hasDistance = distance !== undefined && distance !== null;
                        const isVeryClose = hasDistance && distance < 50;
                        const isClose = hasDistance && distance >= 50 && distance < 150;
                        
                        // Format distance for display (convert meters to feet)
                        let distanceDisplay = '';
                        if (hasDistance) {
                          const distanceFeet = Math.round(distance * 3.28084);
                          if (distanceFeet < 1000) {
                            distanceDisplay = `${distanceFeet}ft`;
                          } else {
                            // Show miles for distances over 1000 feet
                            const distanceMiles = (distanceFeet / 5280).toFixed(1);
                            distanceDisplay = `${distanceMiles}mi`;
                          }
                        }
                        
                        return (
                          <div
                            key={index}
                            data-business-index={index}
                            className="h-12 flex items-center justify-center px-4 cursor-pointer"
                            style={{ scrollSnapAlign: 'center', scrollSnapStop: 'always' }}
                            onClick={() => {
                              setSelectedBusiness({
                                name: business.name,
                                address: business.address,
                                phone: business.phone,
                                fullBusiness: business,
                                isManual: false
                              });
                              
                              // Scroll to center this item
                              const container = scrollContainerRef.current;
                              if (container) {
                                const itemElement = container.querySelector(`[data-business-index="${index}"]`);
                                if (itemElement) {
                                  itemElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                }
                              }
                            }}
                          >
                            <div className="text-white text-sm text-center leading-snug px-2 w-full flex items-center justify-center gap-2">
                              {/* 🎯 Ultra-close indicator */}
                              {isVeryClose && (
                                <span className="text-[var(--pbfs-green)] text-lg">🎯</span>
                              )}
                              
                              {/* Business name */}
                              <span className="truncate flex-shrink">
                                {business.name}
                              </span>
                              
                              {/* Distance badge */}
                              {hasDistance && (
                                <span 
                                  className={`
                                    flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium
                                    ${isVeryClose 
                                      ? 'bg-[var(--pbfs-green)]/20 text-[var(--pbfs-green)] border border-[var(--pbfs-green)]/40' 
                                      : isClose
                                      ? 'bg-blue-500/20 text-blue-300 border border-blue-500/40'
                                      : 'bg-white/10 text-white/60 border border-white/20'
                                    }
                                  `}
                                >
                                  {distanceDisplay}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Bottom spacer - centers last item */}
                      <div style={{ height: '66px' }} aria-hidden="true"></div>
                    </div>

                    {/* Gradient Masks */}
                    <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-[var(--pbfs-navy)] to-transparent pointer-events-none"></div>
                    <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[var(--pbfs-navy)] to-transparent pointer-events-none"></div>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Action Buttons - Fixed at Bottom */}
            <div className="px-6 pb-6 pt-2 flex-shrink-0 border-t border-white/5">
              {/* Submit Button - Full Width */}
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !selectedBusiness}
                className="w-full bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/90 disabled:bg-gray-600 disabled:opacity-50 text-white py-4 px-6 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-xl disabled:transform-none font-medium text-lg"
              >
                {isSubmitting ? '⏳ Submitting...' : '✓ SUBMIT'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Entry Modal */}
      {showManualEntry && (
        <div className="fixed inset-0 bg-black/95 backdrop-blur-md z-[10000] flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-[var(--pbfs-navy)] backdrop-blur-xl rounded-3xl p-8 border border-white/10 max-w-md w-full relative shadow-2xl animate-scaleIn">
            
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img 
                src="/images/PBFS-white.png" 
                alt="PBFS TagQuest"
                className="w-16 h-auto opacity-80"
              />
            </div>
            
            {/* Title */}
            <h2 className="text-xl font-bold text-[var(--pbfs-green)] mb-2 text-center tracking-wide">
              Enter Location Details
            </h2>
            
            <p className="text-white/60 text-center mb-6 text-sm">
              Add company name, address, phone, and notes (optional)
            </p>
            
            {/* Company Name */}
            <div className="mb-4">
              <div className="text-white/60 text-xs mb-2">Company name</div>
              <input
                type="text"
                value={manualCompanyName}
                onChange={(e) => setManualCompanyName(e.target.value)}
                placeholder="e.g., ABC Company Houston"
                className="w-full px-4 py-4 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]/50 focus:border-[var(--pbfs-green)] transition-all"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleManualSubmit();
                }}
              />
            </div>

            {/* Address */}
            <div className="mb-4">
              <div className="text-white/60 text-xs mb-2">Address</div>
              <input
                type="text"
                value={manualAddress}
                onChange={(e) => setManualAddress(e.target.value)}
                placeholder="e.g., 1717 1/2 East Parmer Lane, Austin, TX 78754"
                className="w-full px-4 py-4 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]/50 focus:border-[var(--pbfs-green)] transition-all"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleManualSubmit();
                }}
              />
            </div>

            {/* Phone */}
            <div className="mb-4">
              <div className="text-white/60 text-xs mb-2">Phone</div>
              <input
                type="tel"
                value={manualPhone}
                onChange={(e) => setManualPhone(e.target.value)}
                placeholder="e.g., (512) 555-0123"
                className="w-full px-4 py-4 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]/50 focus:border-[var(--pbfs-green)] transition-all"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleManualSubmit();
                }}
              />
            </div>

            {/* Notes (optional) - ties into submissionNotes */}
            <div className="mb-6">
              <div className="text-white/60 text-xs mb-2">Notes (optional)</div>
              <textarea
                value={submissionNotes}
                onChange={(e) => setSubmissionNotes(e.target.value)}
                placeholder="e.g., access instructions, extinguisher condition notes..."
                rows={4}
                className="w-full px-4 py-4 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]/50 focus:border-[var(--pbfs-green)] transition-all resize-none"
              />
            </div>
            
            {/* Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleManualSubmit}
                disabled={!manualCompanyName.trim() && !manualAddress.trim()}
                className={`w-full py-4 px-6 rounded-2xl transition-all duration-300 shadow-xl font-medium ${
                  (manualCompanyName.trim() || manualAddress.trim())
                    ? 'bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/90 text-white transform hover:scale-[1.02] active:scale-[0.98]'
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
                }`}
              >
                Continue
              </button>
              
              <button
                onClick={() => {
                  setShowManualEntry(false);
                  setManualCompanyName('');
                  setManualAddress('');
                  setManualPhone('');
                }}
                className="w-full text-white/50 hover:text-white/80 text-sm py-3 transition-all duration-200"
              >
                ← Back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {showNotesModal && (
        <div className="fixed inset-0 bg-black/95 backdrop-blur-md z-[10000] flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-[var(--pbfs-navy)] backdrop-blur-xl rounded-3xl p-8 border border-white/10 max-w-md w-full relative shadow-2xl animate-scaleIn">
            
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img 
                src="/images/PBFS-white.png" 
                alt="PBFS TagQuest"
                className="w-16 h-auto opacity-80"
              />
            </div>
            
            {/* Title */}
            <h2 className="text-xl font-bold text-[var(--pbfs-green)] mb-2 text-center tracking-wide">
              📎 Notes & Attachments
            </h2>
            
            <p className="text-white/60 text-center mb-6 text-sm">
              Add notes, photos, or documents
            </p>
            
            {/* Notes Textarea */}
            <div className="mb-4">
              <div className="text-white/70 text-sm mb-2">Notes (optional)</div>
              <textarea
                value={submissionNotes}
                onChange={(e) => setSubmissionNotes(e.target.value)}
                placeholder="e.g., Extinguisher needs recharge, location changed..."
                rows={4}
                className="w-full px-4 py-3 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]/50 focus:border-[var(--pbfs-green)] transition-all resize-none"
                autoFocus
              />
            </div>
            
            {/* Attachment Options */}
            <div className="mb-6">
              <div className="text-white/70 text-sm mb-3">Additional Images</div>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.multiple = true;
                    input.onchange = (e) => {
                      const files = Array.from(e.target.files);
                      files.forEach(file => {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          if (onAddNotes) {
                            onAddNotes('attachment', {
                              data: event.target.result,
                              name: file.name,
                              type: file.type,
                              timestamp: new Date().toISOString()
                            });
                          }
                        };
                        reader.readAsDataURL(file);
                      });
                    };
                    input.click();
                  }}
                  className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white px-4 py-3 rounded-xl border border-white/20 transition-all"
                >
                  📁 Upload
                </button>
                
                <button
                  onClick={async () => {
                    try {
                      const stream = await navigator.mediaDevices.getUserMedia({ 
                        video: { facingMode: 'environment' } 
                      });
                      setCameraStream(stream);
                      setShowCameraCapture(true);
                      setShowNotesModal(false);
                    } catch (error) {
                      console.error('Camera error:', error);
                      showToast('❌ Camera access denied', 'error');
                    }
                  }}
                  className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white px-4 py-3 rounded-xl border border-white/20 transition-all"
                >
                  📷 Camera
                </button>
              </div>
              
              {attachments && attachments.length > 0 && (
                <div className="mt-3 space-y-2">
                  <div className="text-[var(--pbfs-green)] text-sm font-semibold mb-2">
                    ✓ {attachments.length} attachment{attachments.length !== 1 ? 's' : ''} added
                  </div>
                  {attachments.map((att, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white/5 rounded-lg p-2 border border-white/10">
                      <span className="text-white/80 text-xs truncate flex-1">
                        📎 {att.name || `Image ${idx + 1}`}
                      </span>
                      <button
                        onClick={() => {
                          if (setAttachments) {
                            setAttachments(prev => prev.filter((_, i) => i !== idx));
                          }
                        }}
                        className="text-red-400 hover:text-red-300 ml-2 text-sm"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Buttons */}
            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowNotesModal(false);
                  if (submissionNotes.trim()) {
                    showToast('✅ Notes saved', 'success');
                  }
                }}
                className="w-full bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/90 text-white py-4 px-6 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-xl font-medium"
              >
                {submissionNotes.trim() ? 'Save Notes' : 'Continue'}
              </button>
              
              <button
                onClick={() => {
                  setShowNotesModal(false);
                }}
                className="w-full text-white/50 hover:text-white/80 text-sm py-3 transition-all duration-200"
              >
                ← Back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Map Pin Modal */}
      <MapPinModal
        isVisible={showMapModal}
        userGPS={userGPS}
        onPinDropped={(pinData) => {
          setSelectedBusiness(pinData);
          setShowMapModal(false);
          showToast('✅ Location set from map pin!', 'success');
          playSound('scan', 'pin-confirmed');
        }}
        onClose={() => setShowMapModal(false)}
        showToast={showToast}
      />
      
      {/* Camera Capture Modal for Additional Photos */}
      {showCameraCapture && (
        <div className="fixed inset-0 bg-black z-[10001] flex flex-col">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
          />
          
          <div className="absolute bottom-8 left-0 right-0 flex justify-center gap-4">
            <button
              onClick={() => {
                const video = videoRef.current;
                const canvas = document.createElement('canvas');
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                canvas.getContext('2d').drawImage(video, 0, 0);
                const imageData = canvas.toDataURL('image/jpeg', 0.9);
                
                if (onAddNotes) {
                  onAddNotes('attachment', {
                    data: imageData,
                    name: `additional-${Date.now()}.jpg`,
                    type: 'image/jpeg',
                    timestamp: new Date().toISOString()
                  });
                }
                
                if (cameraStream) {
                  cameraStream.getTracks().forEach(track => track.stop());
                  setCameraStream(null);
                }
                setShowCameraCapture(false);
                setShowNotesModal(true);
                showToast('✅ Photo added', 'success');
              }}
              className="w-16 h-16 rounded-full bg-white border-4 border-[var(--pbfs-green)] hover:scale-110 transition-transform"
            />
            
            <button
              onClick={() => {
                if (cameraStream) {
                  cameraStream.getTracks().forEach(track => track.stop());
                  setCameraStream(null);
                }
                setShowCameraCapture(false);
                setShowNotesModal(true);
              }}
              className="px-6 py-3 bg-white/20 text-white rounded-full"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <style jsx>{`
        /* iOS-Style Picker Scroll */
        .picker-scroll {
          scrollbar-width: none; /* Firefox */
          -ms-overflow-style: none; /* IE/Edge */
          -webkit-overflow-scrolling: touch; /* iOS momentum scrolling */
        }
        
        .picker-scroll::-webkit-scrollbar {
          display: none; /* Chrome/Safari/Opera */
        }
        
        /* Snap scrolling */
        .snap-y {
          scroll-snap-type: y mandatory;
        }
        
        .snap-mandatory {
          scroll-snap-stop: always;
        }
        
        .snap-center {
          scroll-snap-align: center;
        }
      `}</style>
    </>
  );
};

export default SelectAndSubmitModal;

